import { googlePlacesId } from '@util/env';
import { AddressAutocompleteInput, CountryDropdown, notify } from '@yembo/yemblocks-core';
import { TextInput } from '@yembo/yemblocks-core/dist/TextInput';
import { googleValidatePostalCode } from 'src/api/google/googleGeocodeApi';
import { useCurrentRef } from 'src/react/_hooks/useCurrentRef';
import { AddressFields, formatLabel, getCountryRegionCode, getPlaceFields } from 'src/react/_util/addressPlace';
import { noEmptySpace } from 'src/react/_util/validation';
import { parseFormattedAddressByCountry } from 'src/util/googlePlaceMapping';

type LocationSectionProps = {
  location: AddressFields;
  dispatch: (value: Partial<AddressFields>) => void;
  header: string;
  defaultCountry: string;
  className?: string;
};

export const LocationSection = ({
  location,
  dispatch,
  header,
  defaultCountry,
  className = '',
}: LocationSectionProps) => {
  const {
    streetAddress,
    addressLine2,
    gateCode,
    city,
    state,
    postalCode,
    country,
    province,
    prefecture,
    district,
    subdistrict,
  } = location;
  const dispatchRef = useCurrentRef(dispatch);

  const { postalCodeName, fullAddressFields } = getPlaceFields(getCountryRegionCode(country || defaultCountry));

  const handlePlaceSelect = (place: google.maps.places.Place, addressName?: string) => {
    const {
      streetAddress,
      addressLine2,
      city,
      state,
      postalCode,
      country,
      district,
      prefecture,
      province,
      subdistrict,
      postalCodeName,
    } = parseFormattedAddressByCountry(place || {}, addressName) as AddressFields;

    const newPlace = {
      city: city || '',
      country: country || '',
      postalCode: postalCode || '',
      addressLine2: addressLine2 || '',
      streetAddress: streetAddress || '',
      postalCodeName: postalCodeName || '',
      state: state || '',
      province: province || '',
      district: district || '',
      prefecture: prefecture || '',
      subdistrict: subdistrict || '',
    };

    const newCountry = country || defaultCountry;

    const { fullAddressFields } = getPlaceFields(getCountryRegionCode(newCountry));

    dispatchRef.current({
      ...newPlace,
      ...fullAddressFields,
    });
  };

  const handlePostalBlur = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name !== 'postalCode' || !value || !location?.country) return;

    const validationResults = await googleValidatePostalCode(
      value,
      location as unknown as Parameters<typeof googleValidatePostalCode>[1]
    );

    if (!validationResults)
      notify.warning(
        'Google Maps does not recognize the entered postal/zip code. Please double check your input for accuracy.'
      );
  };

  return (
    <div className={`section ${className}`}>
      <p className='bold header'>{header}</p>
      <div className='column'>
        <AddressAutocompleteInput
          googleApiKey={googlePlacesId}
          placeFields={['formattedAddress', 'addressComponents']}
          onAddressSelect={handlePlaceSelect}
          inputProps={{
            label: 'Street',
            name: 'street',
            placeholder: 'Enter a location',
            value: streetAddress ?? '',
            onChange: (event) => dispatch({ streetAddress: event.target.value }),
            pattern: noEmptySpace,
          }}
        />
        <TextInput
          label='Address Line 2'
          value={addressLine2 ?? ''}
          name='addressLine2'
          onChange={(event) => dispatch({ addressLine2: event.currentTarget.value })}
        />
        <div className='inline'>
          <TextInput
            label='Gate Code'
            value={gateCode ?? ''}
            name='GateCode'
            onChange={(event) => dispatch({ gateCode: event.currentTarget.value })}
          />
          <TextInput
            label='City'
            value={city ?? ''}
            name='City'
            onChange={(event) => dispatch({ city: event.currentTarget.value })}
          />
        </div>
        <div className='inline'>
          {fullAddressFields.includes('province') && (
            <TextInput
              label='Province'
              value={province ?? ''}
              name='Province'
              onChange={(event) => dispatch({ province: event.currentTarget.value })}
            />
          )}

          {fullAddressFields.includes('state') && (
            <TextInput
              label='State'
              value={state ?? ''}
              name='State'
              onChange={(event) => dispatch({ state: event.currentTarget.value })}
            />
          )}

          <TextInput
            label={formatLabel(postalCodeName ?? 'postalCode')}
            value={postalCode ?? ''}
            name='postalCode'
            pattern='^([\s\da-zA-Z])*$'
            onChange={(event) =>
              dispatch({
                postalCode: event.currentTarget.value || null,
              })
            }
            onBlur={handlePostalBlur}
          />
        </div>

        {fullAddressFields.some((item) => item === 'district' || item === 'subdistrict') && (
          <div className='inline'>
            {fullAddressFields.includes('district') && (
              <TextInput
                label='District'
                value={district ?? ''}
                name='district'
                onChange={(event) => dispatch({ district: event.currentTarget.value })}
              />
            )}

            {fullAddressFields.includes('subdistrict') && (
              <TextInput
                label='Subdistrict'
                value={subdistrict ?? ''}
                name='subdistrict'
                onChange={(event) => dispatch({ subdistrict: event.currentTarget.value })}
              />
            )}
          </div>
        )}

        {fullAddressFields.includes('prefecture') && (
          <TextInput
            label='Prefecture'
            value={prefecture ?? ''}
            name='prefecture'
            onChange={(event) => dispatch({ prefecture: event.currentTarget.value })}
          />
        )}

        <CountryDropdown
          selectedCountry={country ? country : defaultCountry}
          onSelect={({ value }) => {
            if (value === country) return;

            dispatch({
              country: value,
              state: '',
              province: '',
              district: '',
              prefecture: '',
              subdistrict: '',
            });
          }}
        />
      </div>
    </div>
  );
};
